import type { AppProps } from 'next/app';
import '@/styles/globals.css';
import '../components/registration';
import { ColorProvider } from '@/utils/ColorProvider';
import SentryErrorBoundary from '@/components/SentryErrorBoundary';
import posthog from 'posthog-js';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { PostHogProvider } from 'posthog-js/react';
import { getSubdomain } from '@/utils/getDomain';
import { getTrackingParams } from '@/utils/analytics';
if (typeof window !== 'undefined') {
  const urlParams = new URLSearchParams(window.location.search);
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
    api_host: '/ingest',
    ui_host: 'https://us.posthog.com',
    person_profiles: 'always',
    loaded: ph => {
      ph.group('Domain', getSubdomain());
      ph.group('Project Name', process.env.NEXT_PUBLIC_PROJECT_NAME as string || 'Unknown / Development');
      ph.register({
        environment: process.env.NEXT_PUBLIC_ENVIRONMENT || 'development',
        url: window.location.pathname,
        project_name: process.env.NEXT_PUBLIC_PROJECT_NAME as string || 'Unknown / Development',
        domain: getSubdomain()
      });
      if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'development') {
        ph.debug();
      }
      ph.setPersonProperties({
        ...getTrackingParams(),
        EFID: urlParams.get('efid') || '',
        OID: urlParams.get('oid') || '',
        AID: urlParams.get('aid') || '',
        ef_offer_id: urlParams.get('ef_offer_id') || '',
        ef_affiliate_id: urlParams.get('ef_affiliate_id') || ''
      });
    }
  });
}
export default function App({
  Component,
  pageProps
}: AppProps) {
  const router = useRouter();
  useEffect(() => {
    if (!router.isReady || !posthog) return;

    // Track initial page load
    posthog.capture('$pageview', {
      path: window.location.pathname,
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    });

    // Track route changes
    const handleRouteChange = (url: string) => {
      posthog.capture('$pageview', {
        path: url,
        referrer: document.referrer,
        search: router.query,
        title: document.title
      });
    };

    // Track when user starts to leave a page
    const handleRouteStart = () => {
      posthog.capture('$pageleave', {
        path: router.asPath
      });
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    router.events.on('routeChangeStart', handleRouteStart);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
      router.events.off('routeChangeStart', handleRouteStart);
    };
  }, [router.isReady, posthog]);
  useEffect(() => {
    const handleLoad = () => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

      // Get Web Vitals if available
      let cls = 0;
      const observer = new PerformanceObserver(list => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'layout-shift') {
            cls += (entry as unknown as {
              value: number;
            }).value;
          }
        }
      });
      observer.observe({
        entryTypes: ['layout-shift']
      });
      const fid = performance.getEntriesByName('first-input')?.['0']?.duration;
      const lcp = performance.getEntriesByName('largest-contentful-paint')?.['0']?.startTime;
      posthog.capture('performance_metrics', {
        // Time from start to DOM being interactive
        timeToInteractive: navigation.domInteractive - navigation.fetchStart,
        // Time until everything is loaded
        totalLoadTime: navigation.loadEventEnd - navigation.fetchStart,
        // Time spent processing load event handlers
        loadEventTime: navigation.loadEventEnd - navigation.loadEventStart,
        // Time to first byte
        timeToFirstByte: navigation.responseStart - navigation.requestStart,
        // Additional Core Web Vitals
        cumulativeLayoutShift: cls,
        firstInputDelay: fid,
        largestContentfulPaint: lcp,
        // Additional useful metrics
        domContentLoaded: navigation.domContentLoadedEventEnd - navigation.fetchStart,
        firstPaint: performance.getEntriesByName('first-paint')[0]?.startTime,
        firstContentfulPaint: performance.getEntriesByName('first-contentful-paint')[0]?.startTime
      });
    };
    window.addEventListener('load', handleLoad);
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, []);
  useEffect(() => {
    const handleBeforeUnload = () => {
      posthog.capture('site_exit', {
        path: router.asPath,
        exitTime: new Date().toISOString(),
        timeOnPage: performance.now(),
        // milliseconds since page load
        hasUnsavedChanges: false,
        // you can set this based on your app's state
        exitType: 'browser_close_or_navigation'
      });
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [router.asPath]);
  return <SentryErrorBoundary data-sentry-element="SentryErrorBoundary" data-sentry-component="App" data-sentry-source-file="_app.tsx">
			<PostHogProvider client={posthog} data-sentry-element="PostHogProvider" data-sentry-source-file="_app.tsx">
				<ColorProvider data-sentry-element="ColorProvider" data-sentry-source-file="_app.tsx">
					<Component {...pageProps} data-sentry-element="Component" data-sentry-source-file="_app.tsx" />
					<div style={{
          display: 'none'
        }} id="hidden-form-data"></div>
				</ColorProvider>
			</PostHogProvider>
		</SentryErrorBoundary>;
}